import { useState, useEffect, useCallback } from 'react'
import Head from 'next/head'
import { VIEWS } from '../../../containers/auth/modals'
import { useAuth } from '../../../contexts/auth'
import Accordion from '../../../components/accordion/cb-auto/index'
import FeatureShield from '../../../components/featureShield-cbauto/index'
import Header from '../../../components/layout/components/membership-header'
import Footer from '../../../components/layout/components/membership-footer'
import Button from '../../../components/button/button'
import { useMutation } from '@tanstack/react-query'
import useMemberships from '../../../hooks/use-memberships'
import AddMembership from '../../../containers/memberships/add-membership'
import { addMembership } from '../../../api-client/listing/add-membership'
import ConfirmMembershipSuccess from '../../../containers/memberships/modals/confirm-membership-success'
import { registerUser, requestUserEmailConfirmation } from '../../../api-client/identity'
import RegisterWithMembership from '../../../containers/auth/views/registerWithMembership'
import ConfirmEmail from '../../../containers/auth/views/confirm-email'
import ResendEmailConfirmation from '../../../containers/auth/views/resend-email-confirmation'
import ResendEmailConfirmationSuccess from '../../../containers/auth/views/resend-email-confirmation-success'

const initialAccordionsState = {
  work: {
    title: 'How Does The Program Work?',
    name: 'work',
    open: false,
  },
  benefits: {
    title: 'What Are The Benefits Of The Program?',
    name: 'benefits',
    open: false,
  },
  eligibility: {
    title: 'What Vehicles Are Eligible For Purchase Through The Program?',
    name: 'eligibility',
    open: false,
  },
  savings: {
    title: 'How Much Do I Save Through The Program?',
    name: 'savings',
    open: false,
  },
  powertrain: {
    title: 'What Does The Lifetime Powertrain Limited Warranty Cover?',
    name: 'powertrain',
    open: false,
  },
  powertrainEligibility: {
    title: 'What Vehicles Are Eligible For The Free Lifetime Powertrain Limited Warranty?',
    name: 'powertrainEligibility',
    open: false,
  },
  purchasePowertrain: {
    title: 'Can I Buy The Lifetime Powertrain Limited Warranty For My Own Vehicle?',
    name: 'purchasePowertrain',
    open: false,
  },
  hardship: {
    title: 'What Does The Hardship Protection Plan Cover?',
    name: 'hardship',
    open: false,
  },
  outsideDealer: {
    title: 'Can I Go To A Dealer Outside Of The Network And Use The Benefits Of The Program?',
    name: 'outsideDealer',
    open: false,
  },
  noCertificate: {
    title: 'I Registered But Did Not Receive My Eligibility Certificate.',
    name: 'noCertificate',
    open: false,
  },
  becomeDealer: {
    title: 'How Do I Become A CB Absolute Advantage Network Dealer?',
    name: 'becomeDealer',
    open: false,
  },
}

function AbsoluteAdvantage() {
  const [{ isAuthenticated }, { setAuthModal }] = useAuth()
  const [accordionsState, setAccordionsState] = useState(initialAccordionsState)
  const handleAccordionToggle = useCallback((name, open) => {
    setAccordionsState((prev) => ({ ...prev, [name]: { ...prev[name], open } }))
  }, [])

  const [memberships, refetch] = useMemberships([])
  const [absoluteAdvantageAlreadyRegistered, setAbsoluteAdvantageAlreadyRegistered] = useState(
    false,
  )
  const [isSuccess, setIsSuccess] = useState(false)
  const [registeredSuccessful, setRegisteredSuccessful] = useState(false)
  const [userId, setUserId] = useState(null)

  useEffect(() => {
    if (
      memberships?.memberships?.filter((x) => x.programName === 'CB Absolute Advantage')?.length > 0
    ) {
      setAbsoluteAdvantageAlreadyRegistered(true)
    }
  }, [memberships])

  const {
    mutate: membershipMutation,
    isLoading: addingMembership,
    error: membershipError,
  } = useMutation({
    mutationFn: addMembership,
    onSuccess: () => {
      if (isAuthenticated) {
        refetch()
        setIsSuccess(true)
      }
      setView(VIEWS.ConfirmEmail)
    },
  })

  const handleAddMembershipSubmit = (form) => {
    membershipMutation(form)
  }

  const scrollToRegisterSection = useCallback(() => {
    const timeoutRef = setTimeout(() => {
      const registerSection = document.querySelector('.register-section')
      if (registerSection) registerSection.scrollIntoView({ behavior: 'smooth', block: 'center' })
      clearTimeout(timeoutRef)
    }, 200)
  }, [])

  const [view, setView] = useState(VIEWS.Register)
  const [submittedForm, setSubmittedForm] = useState({ register: {} })

  const {
    mutate: registerMutation,
    isLoading: registeringUser,
    error: registerError,
  } = useMutation({
    mutationFn: registerUser,
    onSuccess: (data) => {
      // to try to give a moment for the reponse to propagate, added a slight delay here...
      const timeoutRef = setTimeout(() => {
        setUserId(data?.id)
        setRegisteredSuccessful(true)
        clearTimeout(timeoutRef)
      }, 1000)
    },
  })

  const { mutate: emailConfiramtionResendMutation } = useMutation(requestUserEmailConfirmation)

  const handleRegisterSubmit = (form) => {
    setSubmittedForm((prev) => ({ ...prev, register: form }))
    registerMutation(form)
  }

  const handleLogin = () => {
    setAuthModal({ open: true, view: VIEWS.Login })
  }

  const onClose = () => {
    setAuthModal({ open: false, view: VIEWS.Login })
  }

  const handleEmailConfirmationResend = useCallback(
    (form) => {
      emailConfiramtionResendMutation(
        {
          email: submittedForm[form].email,
          password: submittedForm[form].password,
        },
        { onSuccess: () => setView(VIEWS.ResendEmailConfirmationSuccess) },
      )
    },
    [emailConfiramtionResendMutation, submittedForm],
  )

  return (
    <>
      <Head>
        <title>Absolute Advantage</title>
        <meta name='description' content='Absolute Advantage Benefits sheet' />
      </Head>
      <div className='root'>
        <Header
          hideRegister={absoluteAdvantageAlreadyRegistered}
          scrollToRegisterSectionOrModal={
            isAuthenticated
              ? scrollToRegisterSection
              : () => {
                  setAuthModal({ open: true, view: VIEWS.Register })
                }
          }
        />
        <div id='Home__Advantage'>
          <svg className='shadowBelowHeader'>
            <linearGradient
              id='shadowBelowHeader'
              spreadMethod='pad'
              x1='0.5'
              x2='0.5'
              y1='0'
              y2='1'
            >
              <stop offset='0' stopColor='#000' stopOpacity='1'></stop>
              <stop offset='1' stopColor='#000' stopOpacity='0'></stop>
            </linearGradient>
            <rect id='shadowBelowHeader' rx='0' ry='0' x='0' y='0' width='100%' height='8'></rect>
          </svg>
          <div className='first-section'>
            <div className='first-left-section'>
              <img
                id='AA-logo'
                src='/images/memberships/cbauto/CBAA_-_Blue_-_Horizontal-crop.png'
                alt=''
              />
              <div id='Your_Protective_Car_Buying_Pro'>
                <span>
                  Your Protective
                  <br />
                  Car Buying Program
                </span>
              </div>
              <div id='Your_membership_benefits_worki'>
                <span>
                  Your membership benefits working for you. It&apos;s the best time to trade in your
                  vehicle and we are here to help! Our members save an average of $5,000 annually
                  through our program.
                </span>
              </div>
              <div className='Register-first-section'>
                {!absoluteAdvantageAlreadyRegistered && (
                  <Button
                    onClick={
                      isAuthenticated
                        ? scrollToRegisterSection
                        : () => {
                            setAuthModal({ open: true, view: VIEWS.Register })
                          }
                    }
                  >
                    <span>Register</span>
                  </Button>
                )}
              </div>
            </div>
            <div className='first-right-section'>
              <img
                id='absolute-advantage-person'
                src='/images/memberships/cbauto/absolute-advantage-person.png'
                srcSet='/images/memberships/cbauto/absolute-advantage-person.png 1x, /images/memberships/cbauto/absolute-advantage-person@2x.png 2x'
                alt=''
              />
            </div>
          </div>
          <div className='Register_banner'>
            <svg className='check_shield' viewBox='0 0 42.267 47'>
              <path
                id='check_shield'
                d='M 42.26689910888672 11.50232028961182 C 42.23020935058594 11.09872817993164 42.09262466430664 10.73183155059814 41.8541374206543 10.40161991119385 C 41.61565017700195 10.07140827178955 41.31296157836914 9.814580917358398 40.94606018066406 9.631129264831543 L 22.17911720275879 0.2751750946044922 C 21.84890365600586 0.09172398597002029 21.50035095214844 -2.864135240088217e-08 21.13344955444336 -2.864135240088217e-08 C 20.76654815673828 -2.864135240088217e-08 20.41799736022949 0.09172398597002029 20.08778381347656 0.2751750946044922 L 1.32084059715271 9.631129264831543 C 0.9539387822151184 9.814580917358398 0.6512488722801208 10.07140827178955 0.4127627015113831 10.40161991119385 C 0.1742765605449677 10.73183155059814 0.03669030964374542 11.09872817993164 0 11.50232028961182 C 0 11.5757007598877 0 12.98825263977051 0 15.74001789093018 C 0 18.49178123474121 0.5595173239707947 21.74798965454102 1.678568363189697 25.50873565673828 C 2.797619342803955 29.26948165893555 4.751343250274658 33.13106536865234 7.539798736572266 37.09360504150391 C 10.36494445800781 41.05615234375 14.58425426483154 44.28483963012695 20.19785499572754 46.77976989746094 C 20.3446159362793 46.85315704345703 20.50054550170898 46.90818786621094 20.66565322875977 46.94487762451172 C 20.83075904846191 46.98157119750977 20.9866886138916 46.99991607666016 21.13344955444336 46.99991607666016 C 21.28021049499512 46.99991607666016 21.43614196777344 46.98157119750977 21.60124778747559 46.94487762451172 C 21.76635551452637 46.90818786621094 21.92228317260742 46.85315704345703 22.06904411315918 46.77976989746094 C 27.68264770507812 44.28483963012695 31.90195465087891 41.05615234375 34.72710418701172 37.09360504150391 C 37.51555633544922 33.13106536865234 39.46928405761719 29.26948165893555 40.58832931518555 25.50873565673828 C 41.70738220214844 21.74798965454102 42.26689910888672 18.49178123474121 42.26689910888672 15.74001789093018 C 42.26689910888672 12.98825263977051 42.26689910888672 11.5757007598877 42.26689910888672 11.50232028961182 Z M 21.13344955444336 42.04676055908203 C 17.1709098815918 40.13887023925781 14.07063388824463 37.77238845825195 11.8325309753418 34.94724273681641 C 9.594428062438965 32.15878677368164 7.934221744537354 29.35202980041504 6.851860523223877 26.52688217163086 C 5.7694993019104 23.70173835754395 5.118258476257324 21.07842826843262 4.898117542266846 18.65687561035156 C 4.677976131439209 16.23532104492188 4.586251735687256 14.43752861022949 4.622942447662354 13.26344108581543 L 21.13344955444336 5.008187294006348 L 37.64395904541016 13.26344108581543 C 37.64395904541016 14.40083885192871 37.53388595581055 16.19862937927246 37.31375122070312 18.65687561035156 C 37.05691528320312 21.07842826843262 36.3873291015625 23.70173835754395 35.30496978759766 26.52688217163086 C 34.22261047363281 29.35202980041504 32.58074569702148 32.17712783813477 30.37933540344238 35.00227737426758 C 28.14123153686523 37.79073333740234 25.05930137634277 40.13887023925781 21.13344955444336 42.04676055908203 Z M 18.76694297790527 24.87583160400391 L 13.37351131439209 19.48239898681641 L 10.07141017913818 22.78450012207031 L 18.76694297790527 31.53507041931152 L 32.19548797607422 18.10652351379395 L 28.89338874816895 14.80442142486572 L 18.76694297790527 24.87583160400391 Z'
              ></path>
            </svg>
            <span>Register Today To Take Advantage Of Your Membership Benefits</span>
          </div>
          <div className='benefits-section'>
            <div id='Membership_Benefits'>Membership Benefits</div>
            <div id='Membership-desc'>
              <span>Free peace of mind benefits with purchase of a new or pre-owned vehicle.</span>
            </div>
            <div id='Choose_between_one_of_our_peac'>
              <span>Choose between one of our peace of mind benefits below:</span>
            </div>
            <div className='Benefit-cards'>
              <div className='Hardship-card'>
                <img
                  className='Hardship-card-img'
                  src='/images/memberships/cbauto/desk.png'
                  srcSet='/images/memberships/cbauto/desk.png 1x, /images/memberships/cbauto/desk@2x.png 2x'
                  alt='Hardship Protection Plan'
                />
                <div className='Hardship-card-container'>
                  <div className='Hardship-card-title'>
                    <span>Hardship Protection Plan</span>
                  </div>
                  <div className='Hardship-card-desc'>
                    <span>
                      Covers your monthly car loan payments for up to 90 days or negative equity up
                      to $5,000 in the unfortunate event of involuntary unemployment, illness, or
                      other qualifying life events.
                    </span>
                  </div>
                  <div className='Hardship-card-button'>
                    {!absoluteAdvantageAlreadyRegistered && (
                      <Button
                        onClick={
                          isAuthenticated
                            ? scrollToRegisterSection
                            : () => {
                                setAuthModal({ open: true, view: VIEWS.Register })
                              }
                        }
                      >
                        Register
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              <div className='Lifetime-card'>
                <img
                  className='Lifetime-card-img'
                  src='/images/memberships/cbauto/worker.png'
                  srcSet='/images/memberships/cbauto/worker.png 1x, /images/memberships/cbauto/worker@2x.png 2x'
                  alt='Lifetime'
                />
                <div className='Lifetime-card-container'>
                  <div className='Lifetime-card-title'>
                    <span>Lifetime Powertrain Limited Warranty</span>
                  </div>
                  <div className='Lifetime-card-desc'>
                    <span>
                      Covers engine and transmission repairs when mechanical breakdowns occur,
                      saving you thousands of dollars in costly repairs for the vehicle&apos;s
                      life.*{' '}
                    </span>
                  </div>
                  <div className='Lifetime-card-button'>
                    {!absoluteAdvantageAlreadyRegistered && (
                      <Button
                        onClick={
                          isAuthenticated
                            ? scrollToRegisterSection
                            : () => {
                                setAuthModal({ open: true, view: VIEWS.Register })
                              }
                        }
                      >
                        Register
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div id='Benefits-conditions'>
              <span>
                *Estimated repair costs for Transmission $4,475 and Engine at $6,442. Repair costs
                vary by vehicle make, model, and repair facility repair rates.
              </span>
            </div>
          </div>
          <div className='features-section'>
            <div id='For_Members_and_Your_Families'>
              <span>For Members and Your Families</span>
            </div>
            <div className='features-tagline'>
              <span>
                We believe you should have as much peace of mind as possible. You will enjoy many
                exclusive savings, discounts, and benefits on new and pre-owned vehicles,
                refinancing, insurance, and service contracts.
              </span>
            </div>
            <div className='features-list'>
              <FeatureShield
                title={'Fair Market Pricing'}
                description={
                  'Pre-negotiated, low, and fair pricing with our trusted dealers ensures you get the right price for that perfect next vehicle.'
                }
                check_shield={'checkShield-AA'}
              />
              <FeatureShield
                title={'Member Experience'}
                description={
                  'Unmatched personal member advocates walk you through the entire auto buying process with concierge-level service, taking on the legwork until you are fully satisfied.'
                }
                check_shield={'checkShield-AA'}
              />
              <FeatureShield
                title={'Network of Dealers'}
                description={
                  'Search our vast inventory of new and pre-owned vehicles online from the comfort of your home-old or on the go on your smartphone. Our trusted handpicked dealers are ready to offer our members the full benefits of the program.'
                }
                check_shield={'checkShield-AA'}
              />
              <FeatureShield
                title={'Auto Insurance'}
                description={
                  'The fastest, easiest and only automated way to compare and save on car insurance. Jerry finds the best, customized quotes from more than 50 insurance carriers in 45 seconds!'
                }
                check_shield={'checkShield-AA'}
              />
              <FeatureShield
                title={'Refinancing'}
                description={
                  'When every dollar counts, you can easily apply for auto refinancing on your current vehicle with NO CREDIT IMPACT. Our members have reduced their rates by up to 6.08% with these competitive refinancing rates, an average of $89 monthly savings!'
                }
                check_shield={'checkShield-AA'}
              />
              <FeatureShield
                title={'Vehicle Service Contract'}
                description={
                  'Member care and car care go hand-in-hand. Enjoy the freedom of a monthly comprehensive repair coverage without the long-term contract, saving our members up to 44%.'
                }
                check_shield={'checkShield-AA'}
              />
            </div>
          </div>
          <div className='faq-section'>
            <div id='Frequently_Asked_Questions'>
              <span>Frequently Asked Questions</span>
            </div>
            <div className='accordions-container'>
              <Accordion
                title={<div className='accordionTitle'>{accordionsState.work.title}</div>}
                open={accordionsState.work.open}
                name={accordionsState.work.name}
                onToggle={handleAccordionToggle}
              >
                <div className='faqtext'>
                  <ol className='faq_ol'>
                    <li>
                      Initiate a vehicle search for a new or used car(s) using our vehicle search.
                    </li>
                    <li>
                      Browse our vast selection of dealer inventory until you find your perfect next
                      vehicle.
                      <ul className='faq_ul'>
                        <li>
                          For additional assistance, you can chat using our chat feature at the
                          bottom right corner, call <b>844-307-8267</b>, email{' '}
                          <a
                            className='wordBreak'
                            href='mailto:memberservices@cbabsoluteadvantage.com'
                          >
                            memberservices@cbabsoluteadvantage.com
                          </a>{' '}
                          or text our member advocates once you have opted in for texting.
                        </li>
                      </ul>
                    </li>
                    <li>
                      Once a vehicle has been found, our member advocates will schedule a test drive
                      and answer any other questions you may have.
                    </li>
                    <li>
                      Once the vehicle sale has been finalized, an eligibility certificate will need
                      to be to issued for the complimentary Peace of Mind Benefit you chose
                      (Lifetime Limited Warranty or Hardship Protection Plan).
                    </li>
                    <li>
                      Send the certificate back to CB Absolute Advantage. We will process your
                      benefit within 48 hours and send your final documents back via email.
                    </li>
                  </ol>
                </div>
              </Accordion>
              <Accordion
                title={<div className='accordionTitle'>{accordionsState.benefits.title}</div>}
                open={accordionsState.benefits.open}
                name={accordionsState.benefits.name}
                onToggle={handleAccordionToggle}
              >
                <div className='faqtext'>
                  <ol className='faq_ol'>
                    <li>
                      Choice of one Complimentary &quot;Peace of Mind&quot; benefit with purchase of
                      a new or pre-owned vehicle, which includes the Lifetime Powertrain Warranty
                      that covers costly repairs of the engine and transmission for free. Or, the
                      Hardship Protection Plan which covers your monthly car loan payments for up to{' '}
                      <b>90 days</b> or negative equity up to <b>$5,000</b> when life&apos;s
                      obstacles knock you off your feet.
                    </li>
                    <li>Pre-negotiated Member Pricing that&apos;s low, and fair.</li>
                    <li>
                      Member support from a team of dedicated member advocates that make your car
                      buying journey a priority, taking on the leg work to help you find your
                      perfect next vehicle.
                    </li>
                    <li>
                      Access to a network of trusted dealerships that value their local community.
                    </li>
                    <li>
                      Refinancing is offered to members that aren&apos;t on the road to a car
                      purchase, but need help finding their road to lower payments. Our partnership
                      with Tresl provides members with a better payment rate saving our members an
                      average of <b>$89* monthly</b>.{' '}
                      <i>
                        *Actual monthly payment savings may vary and are determined by individual
                        applicant&apos;s credit and application profile. Monthly payment savings are
                        determined by interest rate, loan term length, or a combination of both.
                        Actual interest reduction may vary and is determined by an individual
                        applicant&apos;s credit and application profile.
                      </i>
                    </li>
                    <li>
                      Compare quotes and buy insurance online and over the phone with access to the
                      30 top-rated insurance carriers saving our members an average of{' '}
                      <b>$504 a year!</b>{' '}
                      <i>
                        *Results of a national survey of new Answer Financial customers reporting
                        insurance savings in 2020.
                      </i>
                    </li>
                    <li>
                      Enjoy the freedom of a monthly comprehensive repair coverage without the
                      long-term contract, saving our members up to <b>44%</b>.
                    </li>
                  </ol>
                </div>
              </Accordion>
              <Accordion
                title={<div className='accordionTitle'>{accordionsState.eligibility.title}</div>}
                open={accordionsState.eligibility.open}
                name={accordionsState.eligibility.name}
                onToggle={handleAccordionToggle}
              >
                <div className='faqtext'>
                  CB Absolute Advantage benefits may be applied to all domestic brand vehicles
                  including Ford, Chevrolet, Dodge, RAM, Jeep, Chrysler, Buick, GMC, Cadillac, and
                  Lincoln with exceptions on some of the luxury, performance, hybrid, and electric
                  models. Some, but not all benefits may be applied to additional brands that are
                  available within the dealer network such as Toyota, Scion, Nissan, Kia, Hyundai,
                  Mitsubishi, Fiat, and Volkswagen.
                </div>
              </Accordion>
              <Accordion
                title={<div className='accordionTitle'>{accordionsState.savings.title}</div>}
                open={accordionsState.savings.open}
                name={accordionsState.savings.name}
                onToggle={handleAccordionToggle}
              >
                <div className='faqtext'>
                  CB Absolute Advantage has negotiated fair pricing with each dealer based on Kelly
                  Blue Book recommendation. Combining this with our additional partnerships, the
                  average savings a member can expect is around <b>$5,000 annually</b>.
                </div>
              </Accordion>
              <Accordion
                title={<div className='accordionTitle'>{accordionsState.powertrain.title}</div>}
                open={accordionsState.powertrain.open}
                name={accordionsState.powertrain.name}
                onToggle={handleAccordionToggle}
              >
                <div className='faqtext'>
                  The Lifetime Powertrain Limited Warranty covers costly repairs of the engine and
                  transmission when mechanical breakdowns occur, which can save you thousands over
                  the life of the vehicle.
                </div>
              </Accordion>
              <Accordion
                title={
                  <div className='accordionTitle'>
                    {accordionsState.powertrainEligibility.title}
                  </div>
                }
                open={accordionsState.powertrainEligibility.open}
                name={accordionsState.powertrainEligibility.name}
                onToggle={handleAccordionToggle}
              >
                <div className='faqtext'>
                  Only new and pre-owned vehicle brands that are American, Asian Import, and VW,
                  with less than 70,000 miles and 7 years of age qualify. The purchase must be made
                  at an authorized CB Absolute Advantage Program certified dealer to receive the
                  complimentary Limited Warranty.
                </div>
              </Accordion>
              <Accordion
                title={
                  <div className='accordionTitle'>{accordionsState.purchasePowertrain.title}</div>
                }
                open={accordionsState.purchasePowertrain.open}
                name={accordionsState.purchasePowertrain.name}
                onToggle={handleAccordionToggle}
              >
                <div className='faqtext'>
                  The Lifetime Powertrain Limited Warranty is an unprecedented benefit offered only
                  through our program as a complementary product when purchasing a new or pre-owned.
                </div>
              </Accordion>
              <Accordion
                title={<div className='accordionTitle'>{accordionsState.hardship.title}</div>}
                open={accordionsState.hardship.open}
                name={accordionsState.hardship.name}
                onToggle={handleAccordionToggle}
              >
                <div className='faqtext'>
                  The Hardship Protection plan is a complimentary payment protection and vehicle
                  return plan, which will cover your vehicle loan payment for <b>90</b> days or up
                  to <b>$5,000</b> in negative equity with unexpected qualifying life events.
                </div>
              </Accordion>
              <Accordion
                title={<div className='accordionTitle'>{accordionsState.outsideDealer.title}</div>}
                open={accordionsState.outsideDealer.open}
                name={accordionsState.outsideDealer.name}
                onToggle={handleAccordionToggle}
              >
                <div className='faqtext'>
                  If a dealer is not a CB Absolute Advantage Dealer when your purchase is made, you
                  will not be able to receive the benefits of the program on your vehicle purchase.
                  If the dealer&apos;s inventory is unable to be accessed via the CB Absolute
                  Advantage Program Website, they are not a dealer within our network. If you need
                  to verify if the dealership you are working with is a network dealership, give us
                  a call at 844-307-8267 or email us at{' '}
                  <a className='wordBreak' href='mailto:memberservices@cbabsoluteadvantage.com'>
                    memberservices@cbabsoluteadvantage.com
                  </a>
                  .
                </div>
              </Accordion>
              <Accordion
                title={<div className='accordionTitle'>{accordionsState.noCertificate.title}</div>}
                open={accordionsState.noCertificate.open}
                name={accordionsState.noCertificate.name}
                onToggle={handleAccordionToggle}
              >
                <div className='faqtext'>
                  When you submit a registration on your preferred vehicle, you will receive an
                  email with your eligibility certificate attached. If you did not receive this
                  email, please check your spam folder before trying to re-register. If you do not
                  see the email in your spam folder, please contact us by phone at 844-307-8267, or
                  email at{' '}
                  <a className='wordBreak' href='mailto:mbeckman@cbautogroup.com'>
                    mbeckman@cbautogroup.com
                  </a>
                  .
                </div>
              </Accordion>
              <Accordion
                title={<div className='accordionTitle'>{accordionsState.becomeDealer.title}</div>}
                open={accordionsState.becomeDealer.open}
                name={accordionsState.becomeDealer.name}
                onToggle={handleAccordionToggle}
              >
                <div className='faqtext'>
                  Contact our Dealer Development Department via email at{' '}
                  <a className='wordBreak' href='mailto:memberservices@cbabsoluteadvantage.com'>
                    memberservices@cbabsoluteadvantage.com
                  </a>
                </div>
              </Accordion>
            </div>
          </div>
          {isSuccess && isAuthenticated && (
            <ConfirmMembershipSuccess onClose={() => setIsSuccess(false)} />
          )}
          {isAuthenticated && !absoluteAdvantageAlreadyRegistered && (
            <div className='register-section flex justifyCenter alignCenter flexDirectionColumn'>
              <div className='register-wrapper'>
                <div className='flex alignCenter flexDirectionColumn'>
                  <h2 className='title'>Get Started</h2>
                  <p className='description'>Find your membership details to sign up.</p>
                </div>
                <div className='flex alignCenter flexDirectionColumn'>
                  <AddMembership
                    onSubmit={handleAddMembershipSubmit}
                    disabled={addingMembership}
                    serverErrors={membershipError}
                    filteredMembership={
                      memberships?.memberships?.length > 0 && 'CB Absolute Advantage'
                    }
                  />
                </div>
              </div>
            </div>
          )}
          {!isAuthenticated && (
            <div className='register-section flex justifyCenter alignCenter flexDirectionColumn'>
              <div className='register-wrapper'>
                <div className='flex alignCenter flexDirectionColumn'>
                  {view === VIEWS.Register && (
                    <RegisterWithMembership
                      submittedForm={submittedForm.register}
                      onSubmit={handleRegisterSubmit}
                      onMembershipSubmit={handleAddMembershipSubmit}
                      disabled={registeringUser || addingMembership}
                      serverErrors={registerError || membershipError}
                      onLogin={handleLogin}
                      registeredSuccessful={!registerError && registeredSuccessful}
                      userId={userId}
                    />
                  )}
                  {view === VIEWS.ConfirmEmail && (
                    <ConfirmEmail
                      onResend={() => handleEmailConfirmationResend('register')}
                      onClose={onClose}
                      emailAddress={submittedForm.register.email}
                    />
                  )}
                  {view === VIEWS.ResendEmailConfirmation && (
                    <ResendEmailConfirmation
                      onResend={() => handleEmailConfirmationResend('login')}
                      email={submittedForm.login.email}
                    />
                  )}
                  {view === VIEWS.ResendEmailConfirmationSuccess && (
                    <ResendEmailConfirmationSuccess onClose={onClose} />
                  )}
                </div>
              </div>
            </div>
          )}
          <Footer />
        </div>
      </div>

      <style jsx>{`
        .hidden {
          display: none;
        }

        .faq-section {
          margin-bottom: 50px;
        }

        .register-section {
          background-color: rgba(245, 246, 249, 1);
          position: relative;
          padding: 12rem 0;
        }

        .register-wrapper {
          background: white;
          border-radius: 1rem;
          padding: 2rem;
          box-shadow: 0 -6px 0 #fff, 0 1px 6px rgba(0, 0, 0, 0.35);

          .title {
            font-family: Red Hat Display;
            font-style: normal;
            font-weight: bold;
            font-size: 32px;
            color: rgba(21, 42, 70, 1);
          }
          .description {
            text-align: center;
            font-family: Red Hat Display;
            font-style: normal;
            font-weight: normal;
            font-size: 16px;
            color: rgba(21, 42, 70, 1);
          }
        }

        .footer_relative {
          height: 300px;
        }

        :global(.accordionTitle) {
          display: flex;
          align-items: center;
        }

        a.link {
          cursor: pointer;
        }

        .accordions-container {
          padding: 4rem 4rem;
        }

        .faqtext {
          text-align: left;
          font-family: Red Hat Display;
          font-weight: normal;
          font-size: 20px;
          color: #212529;
          :global(a) {
            margin: 1.8rem 0 1rem;
            color: blue;
          }
          padding: 0rem 3rem;
        }

        .faq_ol li {
          display: list-item;
          list-style-type: decimal;
        }
        @media only screen and (max-width: 605px) {
          .wordBreak {
            word-break: break-all;
          }
        }

        .faq_ul li {
          padding: 10px;
          display: list-item;
          list-style-type: circle;
        }

        .mediaViewInfo {
          --web-view-name: Home � Advantage;
          --web-view-id: Home__Advantage;
          --web-scale-on-resize: true;
          --web-enable-deep-linking: true;
        }
        :root {
          --web-view-ids: Home__Advantage;
        }
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          border: none;
        }
        #Home__Advantage {
          width: 100%;
          max-width: 192rem;
          margin-left: auto;
          margin-right: auto;
          margin-top: 55px;
          background-color: rgba(255, 255, 255, 1);
          --web-view-name: Home � Advantage;
          --web-view-id: Home__Advantage;
          --web-scale-on-resize: true;
          --web-enable-deep-linking: true;
        }

        .benefits-section {
          background-color: rgba(245, 246, 249, 1);
          padding: 5vw;
        }
        #Rectangle_97 {
          fill: rgba(245, 246, 249, 1);
        }
        .Rectangle_97 {
          position: absolute;
          overflow: visible;
          width: 1440px;
          height: 766px;
          left: 0px;
          top: 0px;
        }
        #spacer {
          opacity: 0.111;
          mix-blend-mode: multiply;
          position: absolute;
          width: 1440px;
          height: 766px;
          left: 0px;
          top: 0px;
          overflow: visible;
        }

        #Membership_Benefits {
          text-align: center;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 32px;
          color: rgba(21, 42, 70, 1);
        }
        #For_Members_and_Your_Families {
          text-align: center;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 32px;
          color: rgba(21, 42, 70, 1);
        }
        #Frequently_Asked_Questions {
          text-align: center;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 32px;
          color: rgba(21, 42, 70, 1);
          text-align: center;
        }

        #Group {
          overflow: visible;
        }
        .Benefit-cards {
          overflow: hidden;
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 36px;
        }
        .Hardship-card {
          box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2);
          transition: 0.3s;
          border-radius: 5px;
          background-color: white;
          margin: 5rem 0rem;
          max-width: 33vw;
          overflow: hidden;
          min-width: 30rem;
          position: relative;
          padding-bottom: 6rem;
        }
        .Hardship-card:hover {
          box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.2);
        }
        .Hardship-card-img {
          border-radius: 5px 5px 0 0;
          width: 100%;
        }
        .Hardship-card-container {
          padding: 0.5rem 2rem;
        }
        .Hardship-card-title {
          line-height: 32px;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 24px;
          color: rgba(21, 42, 70, 1);
          margin: 1rem 0rem;
        }
        .Hardship-card-desc {
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: normal;
          font-size: 18px;
          color: rgba(21, 42, 70, 1);
        }

        .Lifetime-card {
          box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2);
          transition: 0.3s;
          border-radius: 5px;
          background-color: white;
          margin: 5rem 0rem;
          max-width: 33vw;
          overflow: hidden;
          min-width: 30rem;
          position: relative;
          padding-bottom: 6rem;
        }
        .Lifetime-card:hover {
          box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.2);
        }
        .Lifetime-card-img {
          border-radius: 5px 5px 0 0;
          width: 100%;
        }
        .Lifetime-card-container {
          padding: 0.5rem 2rem;
        }
        .Lifetime-card-title {
          line-height: 32px;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 24px;
          color: rgba(21, 42, 70, 1);
          margin: 1rem 0rem;
        }
        .Lifetime-card-desc {
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: normal;
          font-size: 18px;
          color: rgba(21, 42, 70, 1);
        }
        .Hardship-card,
        .Lifetime-card {
          max-width: 460px;
        }

        #Membership-desc {
          text-align: center;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: normal;
          font-size: 18px;
          color: rgba(21, 42, 70, 1);
          margin-top: 3rem;
        }
        .features-tagline {
          text-align: center;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: normal;
          font-size: 1.8rem;
          color: rgba(21, 42, 70, 1);
          margin-top: 2.5rem;
        }
        #Benefits-conditions {
          text-align: center;
          font-family: Red Hat Display;
          font-style: italic;
          font-weight: normal;
          font-size: 14px;
          color: rgba(21, 42, 70, 1);
        }
        .features-section {
          margin: 5rem 5rem;
        }
        .features-list {
          margin-top: 5rem;
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
        }

        #Choose_between_one_of_our_peac {
          text-align: center;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 2rem;
          color: rgba(21, 42, 70, 1);
          margin-top: 5rem;
        }
        #Repeat_Grid_2 {
          position: absolute;
          width: 1360px;
          height: 365px;
          left: 40px;
          top: 1996px;
          overflow: hidden;
        }
        #Group_b {
          position: absolute;
          width: 415px;
          height: 152px;
          left: 0px;
          top: 0px;
          overflow: visible;
        }
        #Pre-negotiated_low_and_fair_pr {
          left: 38px;
          top: 42px;
          position: absolute;
          overflow: visible;
          width: 378px;
          height: 110px;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: normal;
          font-size: 16px;
          color: rgba(21, 42, 70, 1);
        }
        #Fair_Market_Pricing {
          left: 38px;
          top: 0px;
          position: absolute;
          overflow: visible;
          width: 378px;
          height: 31px;
          line-height: 32px;
          margin-top: -4px;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 24px;
          color: rgba(21, 42, 70, 1);
        }
        #e2fa {
          fill: rgba(14, 119, 239, 1);
        }
        .e2fa {
          overflow: visible;
          position: absolute;
          width: 22.505px;
          height: 27.546px;
          left: 0px;
          top: 1.727px;
          transform: matrix(1, 0, 0, 1, 0, 0);
        }
        #Group_ca {
          position: absolute;
          width: 415px;
          height: 152px;
          left: 472px;
          top: 0px;
          overflow: visible;
        }
        #Unmatched_personal_member_advo {
          left: 38px;
          top: 42px;
          position: absolute;
          overflow: visible;
          width: 378px;
          height: 110px;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: normal;
          font-size: 16px;
          color: rgba(21, 42, 70, 1);
        }
        #Member_Experience {
          left: 38px;
          top: 0px;
          position: absolute;
          overflow: visible;
          width: 378px;
          height: 31px;
          line-height: 32px;
          margin-top: -4px;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 24px;
          color: rgba(21, 42, 70, 1);
        }
        #e2fa_b {
          fill: rgba(14, 119, 239, 1);
        }
        .e2fa_b {
          overflow: visible;
          position: absolute;
          width: 22.505px;
          height: 27.546px;
          left: 0px;
          top: 1.727px;
          transform: matrix(1, 0, 0, 1, 0, 0);
        }
        #Group_cb {
          position: absolute;
          width: 415px;
          height: 152px;
          left: 944px;
          top: 0px;
          overflow: visible;
        }
        #Search_our_vast_inventory_of_n {
          left: 38px;
          top: 42px;
          position: absolute;
          overflow: visible;
          width: 378px;
          height: 110px;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: normal;
          font-size: 16px;
          color: rgba(21, 42, 70, 1);
        }
        #Network_of_Dealers {
          left: 38px;
          top: 0px;
          position: absolute;
          overflow: visible;
          width: 378px;
          height: 31px;
          line-height: 32px;
          margin-top: -4px;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 24px;
          color: rgba(21, 42, 70, 1);
        }
        #e2fa_cc {
          fill: rgba(14, 119, 239, 1);
        }
        .e2fa_cc {
          overflow: visible;
          position: absolute;
          width: 22.505px;
          height: 27.546px;
          left: 0px;
          top: 1.727px;
          transform: matrix(1, 0, 0, 1, 0, 0);
        }
        #Group_cd {
          position: absolute;
          width: 415px;
          height: 152px;
          left: 0px;
          top: 208px;
          overflow: visible;
        }
        #The_fastest_easiest_and_only_a {
          left: 38px;
          top: 42px;
          position: absolute;
          overflow: visible;
          width: 378px;
          height: 110px;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: normal;
          font-size: 16px;
          color: rgba(21, 42, 70, 1);
        }
        #Auto_Insurance {
          left: 38px;
          top: 0px;
          position: absolute;
          overflow: visible;
          width: 378px;
          height: 31px;
          line-height: 32px;
          margin-top: -4px;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 24px;
          color: rgba(21, 42, 70, 1);
        }
        #e2fa_cg {
          fill: rgba(14, 119, 239, 1);
        }
        .e2fa_cg {
          overflow: visible;
          position: absolute;
          width: 22.505px;
          height: 27.545px;
          left: 0px;
          top: 1.727px;
          transform: matrix(1, 0, 0, 1, 0, 0);
        }
        #Group_ch {
          position: absolute;
          width: 415px;
          height: 152px;
          left: 472px;
          top: 208px;
          overflow: visible;
        }
        #When_every_dollar_counts_you_c {
          left: 38px;
          top: 42px;
          position: absolute;
          overflow: visible;
          width: 378px;
          height: 110px;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: normal;
          font-size: 16px;
          color: rgba(21, 42, 70, 1);
        }
        #Refinancing {
          left: 38px;
          top: 0px;
          position: absolute;
          overflow: visible;
          width: 378px;
          height: 31px;
          line-height: 32px;
          margin-top: -4px;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 24px;
          color: rgba(21, 42, 70, 1);
        }
        #e2fa_ck {
          fill: rgba(14, 119, 239, 1);
        }
        .e2fa_ck {
          overflow: visible;
          position: absolute;
          width: 22.505px;
          height: 27.545px;
          left: 0px;
          top: 1.727px;
          transform: matrix(1, 0, 0, 1, 0, 0);
        }
        #Group_cl {
          position: absolute;
          width: 415px;
          height: 152px;
          left: 944px;
          top: 208px;
          overflow: visible;
        }
        #Member_care_and_car_care_go_ha {
          left: 38px;
          top: 42px;
          position: absolute;
          overflow: visible;
          width: 378px;
          height: 110px;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: normal;
          font-size: 16px;
          color: rgba(21, 42, 70, 1);
        }
        #Vehicle_Service_Contract {
          left: 38px;
          top: 0px;
          position: absolute;
          overflow: visible;
          width: 378px;
          height: 31px;
          line-height: 32px;
          margin-top: -4px;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 24px;
          color: rgba(21, 42, 70, 1);
        }
        #e2fa_co {
          fill: rgba(14, 119, 239, 1);
        }
        .e2fa_co {
          overflow: visible;
          position: absolute;
          width: 22.505px;
          height: 27.545px;
          left: 0px;
          top: 1.727px;
          transform: matrix(1, 0, 0, 1, 0, 0);
        }
        #df18a16f7007dbcee11c9e71f204af_cp {
          fill: rgba(255, 255, 255, 1);
        }
        .df18a16f7007dbcee11c9e71f204af_cp {
          filter: drop-shadow(0px 3px 6px rgba(0, 0, 0, 0.161));
          /*position: absolute;*/
          position: relative;
          overflow: visible;
          width: 526px;
          height: 503px;
          left: 466px;
          top: 20%;
        }

        #Repeat_Grid_3 {
          position: absolute;
          width: 397px;
          height: 248px;
          left: 521.5px;
          top: 32%;
          overflow: hidden;
        }
        #Group_ct {
          position: absolute;
          width: 397px;
          height: 71px;
          left: 0px;
          top: 0px;
          overflow: visible;
        }
        #Name {
          left: 0px;
          top: 0px;
          position: absolute;
          overflow: visible;
          width: 43px;
          white-space: nowrap;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: normal;
          font-size: 16px;
          color: rgba(21, 42, 70, 1);
        }
        #Path_89 {
          fill: rgba(255, 255, 255, 1);
          stroke: rgba(233, 236, 242, 1);
          stroke-width: 2px;
          stroke-linejoin: miter;
          stroke-linecap: butt;
          stroke-miterlimit: 4;
          shape-rendering: auto;
        }
        .Path_89 {
          overflow: visible;
          position: absolute;
          width: 397px;
          height: 42px;
          left: 0px;
          top: 29px;
          transform: matrix(1, 0, 0, 1, 0, 0);
        }
        #Group_cw {
          position: absolute;
          width: 397px;
          height: 71px;
          left: 0px;
          top: 87px;
          overflow: visible;
        }
        #Email {
          left: 0px;
          top: 0px;
          position: absolute;
          overflow: visible;
          width: 39px;
          white-space: nowrap;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: normal;
          font-size: 16px;
          color: rgba(21, 42, 70, 1);
        }
        #Path_89_cy {
          fill: rgba(255, 255, 255, 1);
          stroke: rgba(233, 236, 242, 1);
          stroke-width: 2px;
          stroke-linejoin: miter;
          stroke-linecap: butt;
          stroke-miterlimit: 4;
          shape-rendering: auto;
        }
        .Path_89_cy {
          overflow: visible;
          position: absolute;
          width: 397px;
          height: 42px;
          left: 0px;
          top: 29px;
          transform: matrix(1, 0, 0, 1, 0, 0);
        }
        #Group_cz {
          position: absolute;
          width: 397px;
          height: 71px;
          left: 0px;
          top: 174px;
          overflow: visible;
        }
        #Membership {
          left: 0px;
          top: 0px;
          position: absolute;
          overflow: visible;
          width: 90px;
          white-space: nowrap;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: normal;
          font-size: 16px;
          color: rgba(21, 42, 70, 1);
        }
        #Path_89_c {
          fill: rgba(255, 255, 255, 1);
          stroke: rgba(233, 236, 242, 1);
          stroke-width: 2px;
          stroke-linejoin: miter;
          stroke-linecap: butt;
          stroke-miterlimit: 4;
          shape-rendering: auto;
        }
        .Path_89_c {
          overflow: visible;
          position: absolute;
          width: 397px;
          height: 42px;
          left: 0px;
          top: 29px;
          transform: matrix(1, 0, 0, 1, 0, 0);
        }
        #Group_164 {
          position: absolute;
          width: 146px;
          height: 42px;
          left: 647px;
          top: 70%;
          overflow: visible;
        }
        #Rectangle_47_c {
          fill: rgba(14, 119, 239, 1);
        }
        .Rectangle_47_c {
          width: 146px;
          height: 42px;
          position: absolute;
          overflow: visible;
          transform: translate(-7px, -0.375px) matrix(1, 0, 0, 1, 7, 0.375) rotate(180deg);
          transform-origin: center;
        }
        #Sign_up {
          left: 45px;
          top: 11px;
          position: absolute;
          overflow: visible;
          width: 57px;
          white-space: nowrap;
          text-align: center;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 16px;
          color: rgba(255, 255, 255, 1);
        }
        #Group_159_c {
          position: absolute;
          width: 1359px;
          height: 75px;
          left: 9px;
          top: 6px;
          overflow: visible;
        }
        #AdobeStock_302279500_Preview {
          fill: rgba(255, 255, 255, 1);
        }
        .AdobeStock_302279500_Preview {
          filter: drop-shadow(0px 3px 6px rgba(0, 0, 0, 0.161));
          position: absolute;
          overflow: visible;
          width: 1377px;
          height: 93px;
          left: 0px;
          top: 0px;
        }
        #f35f {
          fill: rgba(21, 42, 70, 1);
        }
        .f35f {
          overflow: visible;
          position: absolute;
          width: 10.281px;
          height: 5.141px;
          left: 1312.992px;
          top: 37.5px;
          transform: matrix(1, 0, 0, 1, 0, 0);
        }
        #Question_goes_here {
          left: 29px;
          top: 25px;
          position: absolute;
          overflow: visible;
          width: 186px;
          white-space: nowrap;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 20px;
          color: rgba(14, 119, 239, 1);
        }
        #Group_159_da {
          position: absolute;
          width: 1359px;
          height: 75px;
          left: 9px;
          top: 100px;
          overflow: visible;
        }
        #AdobeStock_302279500_Preview_db {
          fill: rgba(255, 255, 255, 1);
        }
        .AdobeStock_302279500_Preview_db {
          filter: drop-shadow(0px 3px 6px rgba(0, 0, 0, 0.161));
          position: absolute;
          overflow: visible;
          width: 1377px;
          height: 93px;
          left: 0px;
          top: 0px;
        }
        #f35f_dc {
          fill: rgba(21, 42, 70, 1);
        }
        .f35f_dc {
          overflow: visible;
          position: absolute;
          width: 10.281px;
          height: 5.141px;
          left: 1312.992px;
          top: 37.5px;
          transform: matrix(1, 0, 0, 1, 0, 0);
        }
        #Question_goes_here_dd {
          left: 29px;
          top: 25px;
          position: absolute;
          overflow: visible;
          width: 186px;
          white-space: nowrap;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 20px;
          color: rgba(14, 119, 239, 1);
        }
        #Group_159_de {
          position: absolute;
          width: 1359px;
          height: 75px;
          left: 9px;
          top: 194px;
          overflow: visible;
        }
        #AdobeStock_302279500_Preview_df {
          fill: rgba(255, 255, 255, 1);
        }
        .AdobeStock_302279500_Preview_df {
          filter: drop-shadow(0px 3px 6px rgba(0, 0, 0, 0.161));
          position: absolute;
          overflow: visible;
          width: 1377px;
          height: 93px;
          left: 0px;
          top: 0px;
        }
        #f35f_dg {
          fill: rgba(21, 42, 70, 1);
        }
        .f35f_dg {
          overflow: visible;
          position: absolute;
          width: 10.281px;
          height: 5.141px;
          left: 1312.992px;
          top: 37.5px;
          transform: matrix(1, 0, 0, 1, 0, 0);
        }
        #Question_goes_here_dh {
          left: 29px;
          top: 25px;
          position: absolute;
          overflow: visible;
          width: 186px;
          white-space: nowrap;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 20px;
          color: rgba(14, 119, 239, 1);
        }
        #Group_159_di {
          position: absolute;
          width: 1359px;
          height: 75px;
          left: 9px;
          top: 288px;
          overflow: visible;
        }
        #AdobeStock_302279500_Preview_dj {
          fill: rgba(255, 255, 255, 1);
        }
        .AdobeStock_302279500_Preview_dj {
          filter: drop-shadow(0px 3px 6px rgba(0, 0, 0, 0.161));
          position: absolute;
          overflow: visible;
          width: 1377px;
          height: 93px;
          left: 0px;
          top: 0px;
        }
        #f35f_dk {
          fill: rgba(21, 42, 70, 1);
        }
        .f35f_dk {
          overflow: visible;
          position: absolute;
          width: 10.281px;
          height: 5.141px;
          left: 1312.992px;
          top: 37.5px;
          transform: matrix(1, 0, 0, 1, 0, 0);
        }
        #Question_goes_here_dl {
          left: 29px;
          top: 25px;
          position: absolute;
          overflow: visible;
          width: 186px;
          white-space: nowrap;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 20px;
          color: rgba(14, 119, 239, 1);
        }
        #Group_159_dm {
          position: absolute;
          width: 1359px;
          height: 75px;
          left: 9px;
          top: 382px;
          overflow: visible;
        }
        #AdobeStock_302279500_Preview_dn {
          fill: rgba(255, 255, 255, 1);
        }
        .AdobeStock_302279500_Preview_dn {
          filter: drop-shadow(0px 3px 6px rgba(0, 0, 0, 0.161));
          position: absolute;
          overflow: visible;
          width: 1377px;
          height: 93px;
          left: 0px;
          top: 0px;
        }
        #f35f_do {
          fill: rgba(21, 42, 70, 1);
        }
        .f35f_do {
          overflow: visible;
          position: absolute;
          width: 10.281px;
          height: 5.141px;
          left: 1312.992px;
          top: 37.5px;
          transform: matrix(1, 0, 0, 1, 0, 0);
        }
        #Question_goes_here_dp {
          left: 29px;
          top: 25px;
          position: absolute;
          overflow: visible;
          width: 186px;
          white-space: nowrap;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 20px;
          color: rgba(14, 119, 239, 1);
        }
        #Group_159_dq {
          position: absolute;
          width: 1359px;
          height: 75px;
          left: 9px;
          top: 476px;
          overflow: visible;
        }
        #AdobeStock_302279500_Preview_dr {
          fill: rgba(255, 255, 255, 1);
        }
        .AdobeStock_302279500_Preview_dr {
          filter: drop-shadow(0px 3px 6px rgba(0, 0, 0, 0.161));
          position: absolute;
          overflow: visible;
          width: 1377px;
          height: 93px;
          left: 0px;
          top: 0px;
        }
        #f35f_ds {
          fill: rgba(21, 42, 70, 1);
        }
        .f35f_ds {
          overflow: visible;
          position: absolute;
          width: 10.281px;
          height: 5.141px;
          left: 1312.992px;
          top: 37.5px;
          transform: matrix(1, 0, 0, 1, 0, 0);
        }
        #Question_goes_here_dt {
          left: 29px;
          top: 25px;
          position: absolute;
          overflow: visible;
          width: 186px;
          white-space: nowrap;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 20px;
          color: rgba(14, 119, 239, 1);
        }
        #Group_159_du {
          position: absolute;
          width: 1359px;
          height: 75px;
          left: 9px;
          top: 570px;
          overflow: visible;
        }
        #AdobeStock_302279500_Preview_dv {
          fill: rgba(255, 255, 255, 1);
        }
        .AdobeStock_302279500_Preview_dv {
          filter: drop-shadow(0px 3px 6px rgba(0, 0, 0, 0.161));
          position: absolute;
          overflow: visible;
          width: 1377px;
          height: 93px;
          left: 0px;
          top: 0px;
        }
        #f35f_dw {
          fill: rgba(21, 42, 70, 1);
        }
        .f35f_dw {
          overflow: visible;
          position: absolute;
          width: 10.281px;
          height: 5.141px;
          left: 1312.992px;
          top: 37.5px;
          transform: matrix(1, 0, 0, 1, 0, 0);
        }
        #What_happens_when_my_vehicle_i {
          left: 29px;
          top: 25px;
          position: absolute;
          overflow: visible;
          width: 369px;
          white-space: nowrap;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 20px;
          color: rgba(14, 119, 239, 1);
        }
        .Register_banner {
          width: 100%;
          text-align: center;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 35px;
          color: rgba(255, 255, 255, 1);
          text-transform: capitalize;
          background-color: rgba(14, 119, 239, 1);
          padding: 5rem 2rem;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        #check_shield {
          fill: rgba(255, 255, 255, 1);
        }
        .check_shield {
          left: 70.433px;
          top: 815px;
          transform: matrix(1, 0, 0, 1, 0, 0);
          height: 5rem;
          margin-right: 1rem;
          margin-left: 2rem;
          min-width: 45px;
        }
        #Lease {
          left: 1409px;
          top: 1634px;
          position: absolute;
          overflow: visible;
          width: 49px;
          white-space: nowrap;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: normal;
          font-size: 18px;
          color: rgba(165, 168, 185, 1);
        }
        .first-section {
          display: flex;
          flex-wrap: wrap;
        }
        .first-left-section {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        .first-right-section {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: start;
          align-items: end;
          overflow: hidden;
          max-height: 70vh;
          min-height: 50vh;
          min-width: 50vw;
        }
        #AA-logo {
          max-width: 50rem;
          margin-left: 1rem;
          margin-bottom: 2rem;
        }
        #Your_Protective_Car_Buying_Pro {
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 54px;
          color: rgba(21, 42, 70, 1);
          margin-left: 3rem;
          margin-bottom: 3rem;
        }
        #Your_membership_benefits_worki {
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: normal;
          font-size: 22px;
          color: rgba(21, 42, 70, 1);
          margin-left: 1rem;
          margin-bottom: 3rem;
          padding: 0rem 2rem;
        }
        #absolute-advantage-person {
        }
        #Header {
          top: 0;
          width: 100%;
          height: 93px;
          display: flex;
          justify-content: end;
          align-items: center;
          flex-wrap: wrap;
          background-color: white;
        }
        #shadowBelowHeader {
          opacity: 0.063;
          fill: url(#shadowBelowHeader);
        }
        .shadowBelowHeader {
          overflow: visible;
          width: 100%;
          height: 8px;
          left: 0px;
          top: 85px;
        }
        #Benefits {
          overflow: visible;
          white-space: nowrap;
          text-align: right;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 16px;
          color: rgba(21, 42, 70, 1);
          margin: 1%;
        }
        #About_d {
          width: 49px;
          white-space: nowrap;
          text-align: right;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 16px;
          color: rgba(21, 42, 70, 1);
          margin: 1%;
        }
        #FAQs {
          overflow: visible;
          margin: 1%;
          white-space: nowrap;
          text-align: right;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 16px;
          color: rgba(21, 42, 70, 1);
        }
        .registerHeader {
          background-color: rgba(14, 119, 239, 1);
          padding: 1em 3em;
          color: white;
          margin: 1%;
          text-align: center;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 1.3rem;
          border-radius: 2rem;
          border-collapse: separate;
          overflow: hidden;
          perspective: 1px;
        }
        .Hardship-card-button,
        .Lifetime-card-button {
          width: fit-content;
          position: absolute;
          bottom: 1.5rem;
        }
        .Register-first-section {
          margin-left: 3rem;
          margin-bottom: 2rem;
        }

        .findVehiclesHeader {
          background-color: rgba(21, 42, 70, 1);
          padding: 1em 2em;
          color: white;
          margin: 1%;
          text-align: center;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 1.3rem;
          border-radius: 2rem;
          border-collapse: separate;
          overflow: hidden;
          perspective: 1px;
        }
        .logo-header {
          margin-right: auto;
          padding-left: 1.3rem;
          width: 223px;
        }

        #Group_54 {
          position: absolute;
          width: 146px;
          height: 42px;
          left: 1254px;
          top: 22px;
          overflow: visible;
        }
        #Rectangle_47_ec {
          fill: rgba(21, 42, 70, 1);
        }
        .Rectangle_47_ec {
          width: 146px;
          height: 42px;
          position: absolute;
          overflow: visible;
          transform: translate(-7px, -0.375px) matrix(1, 0, 0, 1, 7, 0.375) rotate(180deg);
          transform-origin: center;
        }
        #Find_Vehicles {
          left: 23px;
          top: 11px;
          position: absolute;
          overflow: visible;
          width: 101px;
          white-space: nowrap;
          text-align: center;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 16px;
          color: rgba(255, 255, 255, 1);
        }
        #Group_160 {
          position: absolute;
          width: 146px;
          height: 42px;
          left: 1097px;
          top: 22px;
          overflow: visible;
        }
        #Rectangle_47_ef {
          fill: rgba(14, 119, 239, 1);
        }
        .Rectangle_47_ef {
          width: 146px;
          height: 42px;
          position: absolute;
          overflow: visible;
          transform: translate(-7px, -0.375px) matrix(1, 0, 0, 1, 7, 0.375) rotate(180deg);
          transform-origin: center;
        }
        #Register_eg {
          left: 41px;
          top: 11px;
          position: absolute;
          overflow: visible;
          width: 65px;
          white-space: nowrap;
          text-align: center;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 16px;
          color: rgba(255, 255, 255, 1);
          cursor: pointer;
        }
        .footer {
          background-color: white;
        }
        .footer-first-section {
          display: flex;
          justify-content: space-between;
          flex-wrap: wrap;
          margin-top: 2rem;
          margin-bottom: 2rem;
        }
        .service-links {
          display: flex;
          justify-content: start;
          margin-top: 2rem;
          flex-wrap: wrap;
        }
        .social-media-links {
          display: flex;
          justify-content: end;
          margin-top: 2rem;
          flex-wrap: wrap;
        }
        #Buy_a_Vehicle {
          width: 113px;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 18px;
          color: rgba(0, 0, 0, 1);
          margin-left: 3rem;
        }
        #Sell_a_Vehicle {
          width: 111px;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 18px;
          color: rgba(0, 0, 0, 1);
          margin-left: 3rem;
        }
        #Get_Verified {
          width: 105px;
          white-space: nowrap;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 18px;
          color: rgba(0, 0, 0, 1);
          margin-left: 3rem;
        }
        #Need_Help {
          width: 99px;
          white-space: nowrap;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: bold;
          font-size: 18px;
          color: rgba(0, 0, 0, 1);
          margin-left: 3rem;
        }
        #About {
          width: 45px;
          white-space: nowrap;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: normal;
          font-size: 16px;
          color: rgba(0, 0, 0, 1);
          margin-left: 3rem;
        }
        #trademark {
          text-align: right;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: normal;
          font-size: 16px;
          color: rgba(0, 0, 0, 1);
          margin-left: auto;
          margin-right: 3rem;
        }
        #Support {
          width: 59px;
          white-space: nowrap;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: normal;
          font-size: 16px;
          color: rgba(0, 0, 0, 1);
          margin-left: 3rem;
        }
        #Contact {
          width: 58px;
          white-space: nowrap;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: normal;
          font-size: 16px;
          color: rgba(0, 0, 0, 1);
          margin-left: 3rem;
        }
        #Sign_In {
          width: 48px;
          white-space: nowrap;
          text-align: left;
          font-family: Red Hat Display;
          font-style: normal;
          font-weight: normal;
          font-size: 16px;
          color: rgba(0, 0, 0, 1);
          margin-left: 3rem;
        }
        #Line_1 {
          fill: transparent;
          stroke: rgba(233, 236, 242, 1);
          stroke-width: 1px;
          stroke-linejoin: miter;
          stroke-linecap: butt;
          stroke-miterlimit: 4;
          shape-rendering: auto;
        }
        .Line_1 {
          width: 100%;
          height: 1rem;
          transform: matrix(1, 0, 0, 1, 0, 0);
        }
        #fb-icon {
          fill: rgba(14, 119, 239, 1);
        }
        .fb-icon {
          width: 33.719px;
          height: 33.719px;
          transform: matrix(1, 0, 0, 1, 0, 0);
          margin-right: 2rem;
          margin-left: 3rem;
        }
        #twitter-icon {
          fill: rgba(14, 119, 239, 1);
        }
        .twitter-icon {
          width: 33.719px;
          height: 33.719px;
          transform: matrix(1, 0, 0, 1, 0, 0);
          margin-right: 2rem;
        }
        #instagram-icon {
          fill: rgba(14, 119, 239, 1);
        }
        .instagram-icon {
          width: 33.719px;
          height: 33.719px;
          transform: matrix(1, 0, 0, 1, 0, 0);
          margin-right: 2rem;
        }
        .footer-second-section {
          display: flex;
          justify-content: start;
          margin-top: 2rem;
          margin-bottom: 5rem;
          flex-wrap: wrap;
        }
        @media (max-width: 1200px) {
          #AA-logo {
            max-width: 26rem;
          }
          #Your_Protective_Car_Buying_Pro {
            font-size: 30px;
          }
          #Your_membership_benefits_worki,
          .Hardship-card-desc,
          .Lifetime-card-desc,
          .features-tagline,
          .faq_ol,
          .faq_ul,
          .faqtext {
            font-size: 16px;
          }
          .Register_banner,
          #For_Members_and_Your_Families,
          #Frequently_Asked_Questions,
          #Membership_Benefits {
            font-size: 26px;
          }
          #Choose_between_one_of_our_peac,
          #Membership-desc,
          .Hardship-card-title,
          .Lifetime-card-title {
            font-size: 18px;
          }
          #Benefits-conditions {
            font-size: 11px;
          }
        }
      `}</style>
    </>
  )
}

AbsoluteAdvantage.getLayout = (page) => <>{page}</>

export default AbsoluteAdvantage
